/**
 * The comprehensive teacher / school dashboard. One scrollable overview (class
 * header, KPIs, AI next best actions, mastery map, topic rollup, attention list,
 * gradebook) with drill-in to a per-learner profile or per-topic item analysis.
 *
 * Driven entirely by the demo dataset (mockData.ts), which is shaped exactly like
 * the signals we already capture, so this is a faithful "switch-on ready" preview.
 */

import { useState } from 'react';
import { Users, Target, CheckCircle2, AlertTriangle, Activity, GraduationCap, KeyRound, ArrowLeft } from 'lucide-react';
import type { ClassRoster, Learner } from './mockData';
import { DEMO_CLASS, classStats, classActions } from './mockData';
import { StatCard } from './charts';
import { NextBestActions } from './NextBestActions';
import { MasteryHeatmap } from './MasteryHeatmap';
import { TopicBreakdown } from './TopicBreakdown';
import { AttentionList } from './AttentionList';
import { ResultsTable } from './ResultsTable';
import { StudentProfile } from './StudentProfile';
import { ItemAnalysis } from './ItemAnalysis';
import { StudentPortal } from './StudentPortal';
import { ParentPortal } from './ParentPortal';
import { LensSwitch, type Lens } from './portalShell';

type View =
  | { type: 'overview' }
  | { type: 'student'; learner: Learner; lens: Lens }
  | { type: 'item'; topicId: string };

export function TeacherDashboard({ roster = DEMO_CLASS }: { roster?: ClassRoster }) {
  const [view, setView] = useState<View>({ type: 'overview' });
  const back = () => setView({ type: 'overview' });

  if (view.type === 'student') {
    return (
      <div className="pt-8">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <button onClick={back} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#6D28D9] transition-colors hover:text-[#7C3AED] dark:text-[#B69BF5]">
            <ArrowLeft className="h-4 w-4" /> Back to class
          </button>
          <LensSwitch active={view.lens} onChange={(lens) => setView({ ...view, lens })} />
        </div>
        {view.lens === 'teacher'
          ? <StudentProfile roster={roster} learner={view.learner} onSelectTopic={(topicId) => setView({ type: 'item', topicId })} />
          : view.lens === 'student'
            ? <StudentPortal roster={roster} learner={view.learner} />
            : <ParentPortal roster={roster} learner={view.learner} />}
      </div>
    );
  }
  if (view.type === 'item') {
    return <ItemAnalysis roster={roster} topicId={view.topicId} onBack={back} />;
  }

  const stats = classStats(roster);
  const actions = classActions(roster);
  const openLearner = (learner: Learner) => setView({ type: 'student', learner, lens: 'teacher' });
  const openTopic = (topicId: string) => setView({ type: 'item', topicId });

  return (
    <div className="pt-8">
      {/* Class header */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
        <div className="flex items-center gap-4">
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[#2F8F80]/12">
            <GraduationCap className="h-7 w-7 text-[#2F8F80]" />
          </span>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="font-serif text-[26px] font-medium tracking-tight">{roster.name}</h1>
              <span className="rounded-full border border-[#7C3AED]/30 bg-[#7C3AED]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#6D28D9] dark:text-[#B69BF5]">Live preview · sample class</span>
            </div>
            <div className="mt-0.5 text-[13px] text-[#8A8593]">
              {roster.grade} · {roster.subject} · {roster.teacherName} · {roster.schoolName}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-xl border border-black/10 bg-black/[0.02] px-3.5 py-2 dark:border-white/10 dark:bg-white/[0.04]">
          <KeyRound className="h-3.5 w-3.5 text-[#8A8593]" />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-[#8A8593]">Class code</span>
          <span className="font-mono text-[13px] font-bold tracking-widest">{roster.joinCode}</span>
        </div>
      </div>

      {/* KPIs */}
      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatCard icon={<Users />} label="Learners" value={stats.learnerCount} sub={`${stats.topicCount} topics covered`} />
        <StatCard icon={<Target />} label="Avg mastery" value={`${stats.avgMasteryPercent}%`} sub={`${stats.avgScorePercent}% avg assessment`} />
        <StatCard icon={<CheckCircle2 />} label="Completion" value={`${stats.completionPercent}%`} sub="finished the module" />
        <StatCard icon={<AlertTriangle />} label="Need attention" value={stats.needingInterventionCount} sub="flagged learners" tone={stats.needingInterventionCount ? 'warn' : 'good'} />
        <StatCard icon={<Activity />} label="Concept gaps" value={stats.flaggedConceptCount} sub="class-wide weak spots" />
      </div>

      {/* Class next best actions — the headline */}
      <div className="mt-8">
        <NextBestActions scope="class" actions={actions} columns={2}
          title="Next best actions for the class"
          sub="Ranked by impact, grounded in what learners actually got wrong"
          onOpenTopic={openTopic} />
      </div>

      {/* Mastery map */}
      <div className="mt-8">
        <MasteryHeatmap roster={roster} onSelectLearner={openLearner} onSelectTopic={openTopic} />
      </div>

      {/* Topic rollup + attention */}
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1.5fr_1fr]">
        <TopicBreakdown roster={roster} onSelectTopic={openTopic} />
        <AttentionList roster={roster} onSelectLearner={openLearner} />
      </div>

      {/* Gradebook */}
      <div className="mt-4">
        <ResultsTable roster={roster} onSelectLearner={openLearner} />
      </div>
    </div>
  );
}
